import {
  CTCSS_TONES,
  DTCS_CODES,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const BLOCK_SIZE = 32
const MEMORY_SIZE = 0x22a0
const CHANNEL_COUNT = 200
const CHANNEL_SIZE = 32
const NAME_START = 0x1960
const USED_START = 0x1180
const SKIP_START = 0x11a0

type THUV88Spec = {
  id: string
  vendor: string
  model: string
  summary: string
  code: string
}

export type THUV88Profile = {
  protocol: 'thuv88'
  id: string
  vendor: string
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 57600
  channelMinimum: 1
  channelMaximum: 200
  nameLength: 16
  memorySize: number
  code: readonly number[]
  powerLevels: readonly string[]
}

const SPECS: readonly THUV88Spec[] = [
  { id: 'tyt-th-uv88', vendor: 'TYT', model: 'TH-UV88', summary: 'TYT TH-UV88 dual-band handheld.', code: 'UV88' },
  { id: 'retevis-rt85', vendor: 'Retevis', model: 'RT85', summary: 'Retevis RT85 dual-band handheld.', code: 'UV88' },
  { id: 'retevis-ra89', vendor: 'Retevis', model: 'RA89', summary: 'Retevis RA89 dual-band handheld.', code: 'UV99' },
  { id: 'explorer-qrz-1', vendor: 'Explorer', model: 'QRZ-1', summary: 'Explorer QRZ-1 dual-band handheld.', code: 'UV78' },
  { id: 'retevis-p2', vendor: 'Retevis', model: 'P2', summary: 'Retevis P2 compact dual-band handheld.', code: 'UV29' },
  { id: 'retevis-p62', vendor: 'Retevis', model: 'P62', summary: 'Retevis P62 compact dual-band handheld.', code: 'UV29' },
  { id: 'tyt-th-uv98', vendor: 'TYT', model: 'TH-UV98', summary: 'TYT TH-UV98 dual-band handheld.', code: 'UV98' },
]

export const THUV88_PROFILES: readonly THUV88Profile[] = SPECS.map((spec) => ({
  protocol: 'thuv88',
  id: spec.id,
  vendor: spec.vendor,
  model: spec.model,
  summary: spec.summary,
  tags: ['Experimental', 'Read + write', '200 channels', '16-character names'],
  baudRate: 57600,
  channelMinimum: 1,
  channelMaximum: CHANNEL_COUNT,
  nameLength: 16,
  memorySize: MEMORY_SIZE,
  code: Array.from(spec.code, (character) => character.charCodeAt(0)),
  powerLevels: ['High', 'Mid', 'Low'],
}))

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'NFM',
    power: 'High',
    skip: false,
  }
}

function littleU32(raw: Uint8Array): number {
  return raw[0] | (raw[1] << 8) | (raw[2] << 16) | (raw[3] << 24)
}

function setLittleU32(raw: Uint8Array, value: number): void {
  raw[0] = value & 0xff
  raw[1] = (value >> 8) & 0xff
  raw[2] = (value >> 16) & 0xff
  raw[3] = (value >> 24) & 0xff
}

function statusBit(image: Uint8Array, start: number, number: number): boolean {
  const index = number - 1
  return (image[start + Math.floor(index / 8)] & (1 << (index & 7))) !== 0
}

function setStatusBit(image: Uint8Array, start: number, number: number, value: boolean): void {
  const index = number - 1
  const offset = start + Math.floor(index / 8)
  const bit = 1 << (index & 7)
  image[offset] = value ? image[offset] | bit : image[offset] & ~bit
}

type DecodedTone = {
  mode: 'none' | 'ctcss' | 'dtcs'
  ctcss: number | null
  dtcs: number | null
  polarity: 'N' | 'R'
}

function decodeTone(value: number, inverted: boolean): DecodedTone {
  if (value > 2600) return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
  if (value > 511) return { mode: 'ctcss', ctcss: value / 10, dtcs: null, polarity: 'N' }
  return {
    mode: 'dtcs',
    ctcss: null,
    dtcs: Number(value.toString(8)),
    polarity: inverted ? 'R' : 'N',
  }
}

function encodeTone(channel: WebChannel): readonly [number, number] {
  if (channel.toneMode === 'none') return [0x0fff, 0x0fff]
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    if (!CTCSS_TONES.includes(channel.ctcss as (typeof CTCSS_TONES)[number])) {
      throw new Error('That CTCSS tone is not supported by this radio.')
    }
    const value = Math.round(channel.ctcss * 10)
    return [value, value]
  }
  if (channel.toneMode === 'dtcs' && channel.dtcs) {
    if (!DTCS_CODES.includes(channel.dtcs as (typeof DTCS_CODES)[number])) {
      throw new Error('That DCS code is not supported by this radio.')
    }
    const value = Number.parseInt(channel.dtcs.toString(), 8)
    return [value, value]
  }
  return [0x0fff, 0x0fff]
}

function toneMatches(channel: WebChannel, tone: DecodedTone): boolean {
  if (channel.toneMode === 'none') return tone.mode === 'none'
  if (channel.toneMode === 'ctcss') return tone.mode === 'ctcss' && tone.ctcss === channel.ctcss
  return tone.mode === 'dtcs' && tone.dtcs === channel.dtcs && tone.polarity === channel.dtcsPolarity
}

function decodeName(image: Uint8Array, number: number): string {
  const offset = (number - 1) * CHANNEL_SIZE + 15
  const extra = NAME_START + (number - 1) * 10
  return String.fromCharCode(
    ...image.slice(offset, offset + 6),
    ...image.slice(extra, extra + 10),
  )
    .replace(/\0/g, ' ')
    .trim()
}

export function parseTHUV88Channel(image: Uint8Array, profile: THUV88Profile, number: number): WebChannel {
  if (!statusBit(image, USED_START, number)) return emptyChannel(number)
  const offset = (number - 1) * CHANNEL_SIZE
  const raw = image.slice(offset, offset + CHANNEL_SIZE)
  const rxFrequency = littleU32(raw.slice(0, 4)) * 10
  if (!rxFrequency) return emptyChannel(number)
  const rawTx = littleU32(raw.slice(4, 8))
  const txFrequency = rawTx === 0xffffffff ? null : rawTx * 10
  const rxWord = raw[8] | (raw[9] << 8)
  const txWord = raw[10] | (raw[11] << 8)
  const rxtone = decodeTone(rxWord >> 4, false)
  const txTone = decodeTone(txWord >> 4, (txWord & 0x02) !== 0)
  const selected = txTone.mode === 'none' ? rxtone : txTone
  const modeIndex = (raw[12] >> 2) & 0x03
  return {
    number,
    empty: false,
    name: decodeName(image, number),
    rxFrequency,
    txFrequency,
    toneMode: selected.mode,
    ctcss: selected.ctcss,
    dtcs: selected.dtcs,
    dtcsPolarity: selected.polarity,
    mode: modeIndex === 2 ? 'NFM' : 'FM',
    power: profile.powerLevels[raw[12] & 0x03] ?? 'High',
    skip: !statusBit(image, SKIP_START, number),
  }
}

export function parseTHUV88Channels(image: Uint8Array, profile: THUV88Profile): WebChannel[] {
  return Array.from({ length: CHANNEL_COUNT }, (_, index) => parseTHUV88Channel(image, profile, index + 1))
}

export function applyTHUV88Channel(image: Uint8Array, profile: THUV88Profile, channel: WebChannel): void {
  if (channel.number < 1 || channel.number > CHANNEL_COUNT) throw new Error('Channel must be between 1 and 200.')
  setStatusBit(image, USED_START, channel.number, !channel.empty)
  setStatusBit(image, SKIP_START, channel.number, !channel.empty && !channel.skip)
  const offset = (channel.number - 1) * CHANNEL_SIZE
  const raw = image.subarray(offset, offset + CHANNEL_SIZE)
  const nameOffset = NAME_START + (channel.number - 1) * 10
  if (channel.empty) {
    raw.fill(0xff)
    image.fill(0xff, nameOffset, nameOffset + 10)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  setLittleU32(raw.subarray(0, 4), Math.round(channel.rxFrequency / 10))
  setLittleU32(raw.subarray(4, 8), channel.txFrequency ? Math.round(channel.txFrequency / 10) : 0xffffffff)
  const existingTx = decodeTone(((raw[10] | (raw[11] << 8)) >> 4), (raw[10] & 0x02) !== 0)
  const existingRx = decodeTone(((raw[8] | (raw[9] << 8)) >> 4), false)
  if (!toneMatches(channel, existingTx) || (!toneMatches(channel, existingRx) && existingRx.mode !== 'none')) {
    const [txValue, rxValue] = encodeTone(channel)
    raw[8] = (raw[8] & 0x0f) | ((rxValue & 0x0f) << 4)
    raw[9] = (rxValue >> 4) & 0xff
    raw[10] = (raw[10] & 0x01) | ((channel.dtcsPolarity === 'R' ? 0x02 : 0) | ((txValue & 0x0f) << 4))
    raw[11] = (txValue >> 4) & 0xff
  }
  raw[12] =
    (raw[12] & 0xfc) |
    (Math.max(0, profile.powerLevels.indexOf(channel.power)) & 0x03)
  raw[12] = (raw[12] & 0xf3) | ((channel.mode === 'NFM' ? 2 : 1) << 2)
  raw[13] = channel.name ? raw[13] | 0x20 : raw[13] & ~0x20
  const name = channel.name.replace(/[^\x20-\x7e]/g, '').slice(0, 16).padEnd(16, ' ')
  for (let index = 0; index < 6; index += 1) raw[15 + index] = name.charCodeAt(index)
  image.fill(0x20, nameOffset, nameOffset + 10)
  for (let index = 0; index < 10; index += 1) image[nameOffset + index] = name.charCodeAt(index + 6)
}

function frameMagic(code: readonly number[], command: number): Uint8Array {
  return Uint8Array.from([0xfe, 0xfe, 0xee, 0xef, command, ...code, 0xfd])
}

async function identify(profile: THUV88Profile, transport: RadioTransport): Promise<void> {
  await transport.write(frameMagic(profile.code, 0xe0))
  const response = await transport.readExact(36, 3000)
  const expectedPrefix = [0xfe, 0xfe, 0xef, 0xee, 0xe1]
  if (
    !expectedPrefix.every((value, index) => response[index] === value) ||
    !profile.code.every((value, index) => response[5 + index] === value) ||
    response[35] !== 0xfd
  ) {
    throw new Error(`The connected radio does not identify as ${profile.model}.`)
  }
}

export async function readTHUV88RadioWithTransport(
  profile: THUV88Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await transport.discardInput()
  await identify(profile, transport)
  await transport.write(frameMagic(profile.code, 0xe2))
  if ((await transport.readExact(7, 3000))[5] !== 0x00) throw new Error('The radio refused UV88 read mode.')
  try {
    const image = new Uint8Array(MEMORY_SIZE)
    const total = MEMORY_SIZE / BLOCK_SIZE
    for (let index = 0; index < total; index += 1) {
      const address = index * BLOCK_SIZE
      const request = Uint8Array.from([0xfe, 0xfe, 0xee, 0xef, 0xeb, (address >> 24) & 0xff, (address >> 16) & 0xff, (address >> 8) & 0xff, address & 0xff, 0, BLOCK_SIZE, 0xfd])
      await transport.write(request)
      const response = await transport.readExact(BLOCK_SIZE + 13, 4000)
      image.set(response.slice(11, 11 + BLOCK_SIZE), address)
      onProgress?.({ current: index + 1, total, label: 'Reading TYT UV88 memory' })
    }
    return { image, reportedModel: profile.model }
  } finally {
    await transport.write(frameMagic(profile.code, 0xe5))
  }
}

export async function writeTHUV88RadioWithTransport(
  profile: THUV88Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < MEMORY_SIZE) throw new Error('The selected image is smaller than UV88 memory.')
  await transport.discardInput()
  await identify(profile, transport)
  await transport.write(frameMagic(profile.code, 0xe3))
  const entered = await transport.readExact(7, 3000)
  if (entered[5] !== 0x00) throw new Error('The radio refused UV88 write mode.')
  try {
    const addresses = Array.from({ length: MEMORY_SIZE / BLOCK_SIZE }, (_, index) => index * BLOCK_SIZE)
      .filter((address) => address < 0x1680 || address >= 0x1940)
    for (let index = 0; index < addresses.length; index += 1) {
      const address = addresses[index]
      const output = Uint8Array.from([(address >> 24) & 0xff, (address >> 16) & 0xff, (address >> 8) & 0xff, address & 0xff, 0, BLOCK_SIZE, ...image.slice(address, address + 32)])
      let sum = 0
      for (const value of output) sum = (sum + value) & 0xff
      const request = Uint8Array.from([0xfe, 0xfe, 0xee, 0xef, 0xe4, ...output, sum === 0 ? 0 : 256 - sum, 0xfd])
      await transport.write(request)
      const ack = await transport.readExact(7, 3000)
      if (ack[4] !== 0xe6 || ack[5] !== 0x00 || ack[6] !== 0xfd) throw new Error(`The radio rejected UV88 block 0x${address.toString(16)}.`)
      onProgress?.({ current: index + 1, total: addresses.length, label: 'Writing TYT UV88 memory' })
    }
  } finally {
    await transport.write(frameMagic(profile.code, 0xe5))
  }
}
